import React, { useState, useEffect } from "react";
import {
  useGetCompanySubmissionsQuery,
  useGetTemplateReportQuery,
} from "../../../../services/api";
import AnswersTable from "./AnswersTable";

/**
 * UnpackValuesResults Component
 * Displays Q&A results from diagnostic questionnaires
 * Shows which employees answered which questions and their responses
 * Props:
 * - companyId: Company ID to fetch submissions for
 * - assignedTemplates: Array of templates assigned to this company
 * - selectedTemplateId: Currently selected template ID (controlled from parent)
 * - onSelectTemplate: Callback when template selection changes
 * - expandedQuestions: Object tracking which questions are expanded
 * - setExpandedQuestions: State setter to toggle question expansion
 *
 * Features:
 * - Dropdown to select which form's answers to view
 * - Filter tabs (All, Manager, Staff, Director)
 * - Expandable questions showing answers from employees
 * - Employee avatars and details
 * - Answer preview text
 * - Pagination for results
 */
export default function UnpackValuesResults({ companyId, assignedTemplates = [], selectedTemplateId, onSelectTemplate, expandedQuestions, setExpandedQuestions }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [positionFilter, setPositionFilter] = useState('all');

  // Use internal state if no external control provided
  const [internalTemplateId, setInternalTemplateId] = useState(null);
  const templateId = selectedTemplateId !== undefined ? selectedTemplateId : internalTemplateId;
  const setTemplateId = onSelectTemplate || setInternalTemplateId;

  // Auto-select first template when assignedTemplates loads
  useEffect(() => {
    if (assignedTemplates?.length > 0 && !templateId) {
      setTemplateId(assignedTemplates[0]._id);
    }
  }, [assignedTemplates, templateId, setTemplateId]);

  // Fetch submissions for this company/template
  const { data: submissionsData, isLoading: submissionsLoading } = useGetCompanySubmissionsQuery(
    {
      companyId,
      page: currentPage,
      limit: 50,
      status: 'submitted',
      templateId: templateId,
      includeAnswers: true
    },
    { skip: !companyId || !templateId }
  );

  // Fetch aggregated report for questions
  const { data: reportData, isLoading: reportLoading } = useGetTemplateReportQuery(
    { templateId, companyId },
    { skip: !templateId }
  );

  const submissions = submissionsData?.submissions || [];
  const pagination = submissionsData?.pagination || {};
  const aggregatedQuestions = reportData || [];

  // Filter submissions by search term and position
  const filteredSubmissions = submissions.filter(sub => {
    // Position filter
    if (positionFilter !== 'all') {
      const role = sub.employee?.companyRole || sub.employee?.role?.name || '';
      if (role.toLowerCase() !== positionFilter.toLowerCase()) return false;
    }
    // Search filter
    if (searchTerm) {
      const name = `${sub.employee?.firstName || ''} ${sub.employee?.lastName || ''}`.toLowerCase();
      const email = sub.employee?.email?.toLowerCase() || '';
      if (!name.includes(searchTerm.toLowerCase()) && !email.includes(searchTerm.toLowerCase())) {
        return false;
      }
    }
    return true;
  });

  // Calculate position counts
  const positionCounts = submissions.reduce((acc, sub) => {
    const role = (sub.employee?.companyRole || sub.employee?.role?.name || 'Staff').toLowerCase();
    acc[role] = (acc[role] || 0) + 1;
    return acc;
  }, {});

  const isLoading = submissionsLoading || reportLoading;

  return (
    <div className="cd-results-section">
      <div className="cd-results-header">
        <h3 className="cd-results-title">Unpack Values Results</h3>
        <div className="cd-results-controls">
          {/* Template Dropdown */}
          <select
            className="cd-template-select"
            value={templateId || ''}
            onChange={(e) => setTemplateId(e.target.value)}
          >
            <option value="">Select Form</option>
            {assignedTemplates?.map(template => (
              <option key={template._id} value={template._id}>
                {template.title}
              </option>
            ))}
          </select>

          <input
            className="cd-results-search"
            placeholder="Search By Position / Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="cd-search-hint">Ctrl K</span>
        </div>
      </div>

      <div className="cd-results-tabs">
        <button
          className={`cd-results-tab ${positionFilter === 'all' ? 'cd-results-tab-active' : ''}`}
          onClick={() => setPositionFilter('all')}
        >
          All ({submissions.length})
        </button>
        <button
          className={`cd-results-tab ${positionFilter === 'manager' ? 'cd-results-tab-active' : ''}`}
          onClick={() => setPositionFilter('manager')}
        >
          Manager ({positionCounts['manager'] || 0})
        </button>
        <button
          className={`cd-results-tab ${positionFilter === 'staff' ? 'cd-results-tab-active' : ''}`}
          onClick={() => setPositionFilter('staff')}
        >
          Staff ({positionCounts['staff'] || 0})
        </button>
        <button
          className={`cd-results-tab ${positionFilter === 'director' ? 'cd-results-tab-active' : ''}`}
          onClick={() => setPositionFilter('director')}
        >
          Director ({positionCounts['director'] || 0})
        </button>
      </div>

      {isLoading && (
        <div className="cd-qa-loading">Loading submissions...</div>
      )}

      {!isLoading && aggregatedQuestions.length === 0 && (
        <div className="cd-qa-empty">No submissions found for this template.</div>
      )}

      {!isLoading && aggregatedQuestions.map((question, qIndex) => {
        const isExpanded = expandedQuestions[qIndex];
        return (
          <div className="cd-qa-item" key={qIndex}>
            <div
              className="cd-qa-header"
              onClick={() =>
                setExpandedQuestions((prev) => ({
                  ...prev,
                  [qIndex]: !prev[qIndex],
                }))
              }
            >
              <div className="cd-qa-info">
                <span className="cd-qa-number">Q{question.questionIndex + 1}</span>
                <div className="cd-qa-details">
                  <div className="cd-qa-question">
                    {question.questionText}
                  </div>
                  <div className="cd-qa-meta">Answered by {question.responseCount} People</div>
                </div>
              </div>
              <div
                className={`cd-qa-arrow ${
                  isExpanded ? "cd-qa-arrow-expanded" : ""
                }`}
              >
                &rsaquo;
              </div>
            </div>

            {isExpanded && (
              <div className="cd-qa-content">
                <AnswersTable
                  questionIndex={question.questionIndex}
                  submissions={filteredSubmissions}
                  questionType={question.questionType}
                />

                {pagination.pages > 1 && (
                  <div className="cd-qa-pagination">
                    <span className="cd-qa-page-info">
                      Page {String(pagination.page || 1).padStart(2, '0')} Of {String(pagination.pages || 1).padStart(2, '0')}
                    </span>
                    <div className="cd-qa-page-controls">
                      <button
                        className="cd-qa-page-btn"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        &lsaquo; Prev
                      </button>
                      {[...Array(Math.min(3, pagination.pages || 1))].map((_, i) => (
                        <button
                          key={i + 1}
                          className={`cd-qa-page-num ${currentPage === i + 1 ? 'cd-qa-page-active' : ''}`}
                          onClick={() => setCurrentPage(i + 1)}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button
                        className="cd-qa-page-btn"
                        onClick={() => setCurrentPage(p => p + 1)}
                        disabled={currentPage >= (pagination.pages || 1)}
                      >
                        Next &rsaquo;
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
